/**
 * FP3-106C — One-click Clear All schedule reset modal.
 *
 * Flow: server preview (exact counts + provider/period target) →
 * typed-phrase confirmation → apply → success toast with Undo action.
 * The server re-validates period+provider+phrase+counts — the client
 * gates here are UX, not the security boundary.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CONFIRM_PHRASE = "CLEAR ALL";

const errDetail = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  return e?.message || "Something went wrong.";
};

export default function ClearAllScheduleModal({
  open,
  onOpenChange,
  provider,
  activePeriod,
  onCleared,
}) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      // Fully reset local state whenever the modal closes.
      setLoading(false);
      setApplying(false);
      setPreview(null);
      setPhrase("");
      setError("");
      return;
    }
    if (!provider?.id || !activePeriod?.id) {
      setError("No active schedule period selected.");
      return;
    }
    setLoading(true);
    setError("");
    api
      .clearAllPreview({
        schedule_period_id: activePeriod.id,
      })
      .then((res) => setPreview(res.data))
      .catch((e) => setError(errDetail(e)))
      .finally(() => setLoading(false));
  }, [open, provider?.id, activePeriod?.id]);

  const disabled =
    applying ||
    !preview ||
    (phrase || "").trim() !== CONFIRM_PHRASE ||
    (preview?.counts?.saved_service_blocks ?? 0) < 0;

  const handleUndo = async (clearBatchId) => {
    try {
      await api.clearAllUndo({ clear_batch_id: clearBatchId });
      toast.success("Schedule restored.");
      if (onCleared) await onCleared();
    } catch (e) {
      toast.error(`Could not undo: ${errDetail(e)}`);
    }
  };

  const handleApply = async () => {
    if (!preview) return;
    setApplying(true);
    setError("");
    try {
      const res = await api.clearAllApply({
        schedule_period_id: activePeriod.id,
        preview_token: preview.preview_token,
        confirmation_phrase: CONFIRM_PHRASE,
        expected_saved_service_blocks:
          preview.counts?.saved_service_blocks ?? 0,
      });
      const batchId = res.data?.clear_batch_id;
      onOpenChange(false);
      if (onCleared) await onCleared();
      toast.success("Schedule cleared", {
        description:
          "Your schedule was reset. Undo is available for the next 5 minutes.",
        action: batchId
          ? {
              label: "Undo clear",
              onClick: () => handleUndo(batchId),
            }
          : undefined,
        duration: 8000,
      });
    } catch (e) {
      setError(errDetail(e));
    } finally {
      setApplying(false);
    }
  };

  const counts = preview?.counts || {};
  const savedCount = counts.saved_service_blocks ?? 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !applying && onOpenChange(v)}>
      <DialogContent
        className="max-w-lg"
        data-testid="clear-all-modal"
      >
        <DialogHeader>
          <DialogTitle
            className="text-[#7A2F2F]"
            data-testid="clear-all-modal-title"
          >
            Clear this entire schedule?
          </DialogTitle>
          <DialogDescription className="text-[#4B5563] text-sm leading-relaxed">
            This will remove all saved and unsaved schedule blocks for
            this provider in the selected schedule period, including
            the current Wizard draft. Student records, service
            commitments, rules, personal blocks, and historical
            sessions will not be deleted.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div
            className="text-sm text-[#6B7280]"
            data-testid="clear-all-loading"
          >
            Loading impact preview…
          </div>
        )}

        {!loading && preview && (
          <div
            className="space-y-3"
            data-testid="clear-all-preview-counts"
          >
            <div className="rounded-lg border border-[#E7CFCB] bg-[#FBF3F1] p-3">
              <p className="text-[11px] uppercase tracking-wide text-[#7A2F2F] font-semibold">
                Will be removed
              </p>
              <ul className="mt-2 space-y-1 text-sm text-[#4B2222]">
                <li data-testid="clear-all-count-service">
                  <span className="tabular-nums font-semibold">
                    {savedCount}
                  </span>{" "}
                  saved service block{savedCount === 1 ? "" : "s"}
                </li>
                <li data-testid="clear-all-count-wizard">
                  Current Wizard draft & unsaved Builder edits
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-[#D6E4DA] bg-[#F1F7F3] p-3">
              <p className="text-[11px] uppercase tracking-wide text-[#1E3F33] font-semibold">
                Preserved
              </p>
              <ul className="mt-2 space-y-1 text-sm text-[#1E3F33]">
                <li data-testid="clear-all-preserved-personal">
                  <span className="tabular-nums font-semibold">
                    {counts.preserved_personal_blocks ?? 0}
                  </span>{" "}
                  personal block
                  {counts.preserved_personal_blocks === 1 ? "" : "s"}{" "}
                  (lunch, prep, meetings)
                </li>
                <li data-testid="clear-all-preserved-sessions">
                  <span className="tabular-nums font-semibold">
                    {counts.preserved_historical_sessions ?? 0}
                  </span>{" "}
                  historical session
                  {counts.preserved_historical_sessions === 1 ? "" : "s"}
                </li>
                <li>
                  Students, goals, notes, rules, availability, and all
                  other providers&apos; schedules
                </li>
              </ul>
            </div>

            <div className="text-xs text-[#6B7280]">
              Provider:{" "}
              <span className="font-medium text-[#1F2937]">
                {preview.provider_name}
              </span>
              {" · "}
              Period:{" "}
              <span className="font-medium text-[#1F2937]">
                {preview.schedule_period_name || activePeriod?.name}
              </span>
            </div>

            <div className="pt-1">
              <Label
                htmlFor="clear-all-phrase"
                className="text-xs text-[#374151]"
              >
                Type{" "}
                <span className="font-mono font-semibold text-[#7A2F2F]">
                  {CONFIRM_PHRASE}
                </span>{" "}
                to confirm
              </Label>
              <Input
                id="clear-all-phrase"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                autoComplete="off"
                disabled={applying}
                data-testid="clear-all-phrase-input"
                className="mt-1"
              />
            </div>
          </div>
        )}

        {error && (
          <p
            className="text-sm text-[#7A2F2F] bg-[#FBF3F1] border border-[#E7CFCB] rounded-md px-3 py-2"
            data-testid="clear-all-error"
          >
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applying}
            data-testid="clear-all-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={disabled}
            className="bg-[#7A2F2F] hover:bg-[#5F2323] text-white"
            data-testid="clear-all-confirm-btn"
          >
            {applying ? "Clearing…" : "Clear entire schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
